import { NoddiInput } from '@ui-components/Inputs/NoddiInput';
import { useFieldContext } from '../formContext';
import { TanstackFieldError } from '../TanstackFieldError';
import { useTanstackFieldAria } from '../useTanstackFieldAria';

type Props = Omit<React.ComponentProps<typeof NoddiInput>, 'name' | 'value' | 'defaultValue'>;

export const TanstackFormInput = ({ onChange, onBlur, ...rest }: Props) => {
  const field = useFieldContext<string | number | null>();
  const fieldAria = useTanstackFieldAria(field, rest['aria-describedby']);

  const handleValueChange = (newValue: string) => {
    if (rest.type === 'number' && newValue !== '') {
      const parsed = Number(newValue);
      if (!Number.isFinite(parsed)) {
        field.handleChange(null);
        return;
      }
      field.handleChange(parsed);
    } else if (newValue === '') {
      field.handleChange(rest.type === 'number' ? null : '');
    } else {
      field.handleChange(newValue);
    }
  };

  return (
    <div data-testid='tanstack-form-text-input-container'>
      <NoddiInput
        {...rest}
        id={rest.id ?? field.name}
        name={field.name}
        {...fieldAria}
        value={field.state.value ?? ''}
        onBlur={(event) => {
          // A minimum must not replace intermediate digits while a number is being typed.
          if (rest.type === 'number' && typeof field.state.value === 'number') {
            const max = rest.max === undefined || rest.max === '' ? undefined : Number(rest.max);
            const min = rest.min === undefined || rest.min === '' ? undefined : Number(rest.min);
            const value = field.state.value;
            const clampedMax = max !== undefined && Number.isFinite(max) ? Math.min(value, max) : value;
            const clamped = min !== undefined && Number.isFinite(min) ? Math.max(clampedMax, min) : clampedMax;
            if (clamped !== value) {
              field.handleChange(clamped);
            }
          }
          field.handleBlur();
          onBlur?.(event);
        }}
        onChange={(event) => {
          handleValueChange(event.target.value);
          onChange?.(event);
        }}
      />
      <TanstackFieldError field={field} />
    </div>
  );
};
